import * as React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { Button, SplitButton } from '@progress/kendo-react-buttons';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Skeleton } from '@progress/kendo-react-indicators';
import { OverviewPage, StackLayout, Text } from '../../../ui';
import { ValueItem } from '../../shared/components';
import { loadStudyPlanOverviewPage, approveStudyPlan, deleteStudyPlan, toggleConfirmDialog } from '../actions';
import { StudyPlanCycleList } from '.';
import { withRouter } from '../../../components/withRouter';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

const ApproveText = styled(Text).attrs(props => ({
  variant: 'body2',
  textColor: props.approved ? '#94C533' : '#F1726E'
}))``;

const STUDY_PLAN_ACTIONS = {
  APPROVE: 'Approve',
  EDIT: 'Edit',
  DELETE: 'Delete'
};

class StudyPlanOverviewPage extends React.PureComponent {

  constructor() {
    super();
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleApproveClick = this.handleApproveClick.bind(this);
    this.handleCopyClick = this.handleCopyClick.bind(this);
    this.handleSplitButtonItemClick = this.handleSplitButtonItemClick.bind(this);
    this.renderSplitButtonItem = this.renderSplitButtonItem.bind(this);
    this.handleRemoveConfirmClick = this.handleRemoveConfirmClick.bind(this);
  }

  async componentDidMount() {
    if (this.props.params?.studyPlanId) {
      this.props.loadStudyPlanOverviewPage(this.props.params.studyPlanId);
    }
  }

  render() {
    const { studyPlan, loading } = this.props;
    const localizationService = provideLocalizationService(this);
    return (
      <>
        <OverviewPage
          title={loading
            ? <Skeleton shape='text' style={{ width: 300, height: 28 }}/>
            : `${studyPlan?.studyProgramName} ${studyPlan?.year}${localizationService.toLanguageString('StudyPlan.shorthandYear')}/${studyPlan?.year + 1}${localizationService.toLanguageString('StudyPlan.shorthandYear')}`
          }
          headerActions={
            <SplitButton
              themeColor={'primary'}
              text={localizationService.toLanguageString('custom.copy')}
              items={this.renderSplitButtonItem()}
              onButtonClick={this.handleCopyClick}
              onItemClick={this.handleSplitButtonItemClick}
            />
          }
          breadcrumbItems={[
            {
              id: 'study-plans',
              text: localizationService.toLanguageString('StudyPlan.studyPlans')
            },
            {
              id: 'study-plan',
              text: localizationService.toLanguageString('StudyPlan.studyPlan'),
              disabled: true
            }
          ]}
          onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
          onBackClick={this.handleBackClick}
          summary={
            <StackLayout rowGap='8px'>
              <ValueItem
                label={localizationService.toLanguageString('StudyPlan.confirmed')}
                labelWidth='160px'
                loading={loading}
              >
                <ApproveText approved={studyPlan?.isApproved}>
                  {
                    studyPlan?.isApproved
                      ? localizationService.toLanguageString('common.yes')
                      : localizationService.toLanguageString('common.no')
                  }
                </ApproveText>
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('StudyPlan.credits')}
                labelWidth='160px'
                loading={loading}
              >
                {studyPlan?.studyPlanCycleCredits + studyPlan?.optionalCredits}/{studyPlan?.credits}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('StudyPlan.optionalCredits')}
                labelWidth='160px'
                loading={loading}
              >
                {studyPlan?.optionalCredits}
              </ValueItem>
            </StackLayout>
          }
          tabs={
            <TabStrip selected={0}>
              <TabStripTab title={localizationService.toLanguageString('StudyPlan.cycles')} />
            </TabStrip>
          }
        >
          <StudyPlanCycleList studyPlanId={this.props.params.studyPlanId} studyProgramId={this.props.studyPlan?.studyProgramId}/>
        </OverviewPage>
        {this.props.isConfirmDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.props.toggleConfirmDialog}>
            <Text>{localizationService.toLanguageString('StudyPlan.deleteConfirmationDialogMessage')}</Text>
            <DialogActionsBar>
              <Button themeColor={'primary'} onClick={this.handleRemoveConfirmClick}>{localizationService.toLanguageString('buttons.delete')}</Button>
              <Button onClick={this.props.toggleConfirmDialog}>{localizationService.toLanguageString('buttons.cancel')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  handleEditClick() {
    this.props.navigate(`/study_plans/${this.props.params.studyPlanId}`);
  }

  handleCopyClick() {
    this.props.navigate(`/study_plans/${this.props.params.studyPlanId}/copy`);
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'study-plans') {
      this.props.navigate('/study_plans');
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleApproveClick() {
    this.props.approveStudyPlan(this.props.studyPlan);
  }

  handleRemoveConfirmClick() {
    this.props.deleteStudyPlan(this.props.studyPlan);
  }

  handleSplitButtonItemClick(e) {
    switch (e.item.id) {
      case STUDY_PLAN_ACTIONS.APPROVE:
        this.handleApproveClick();
        break;
      case STUDY_PLAN_ACTIONS.EDIT:
        this.handleEditClick();
        break;
      case STUDY_PLAN_ACTIONS.DELETE:
        this.props.toggleConfirmDialog();
        break;
    }
  }

  renderSplitButtonItem() {
    const localizationService = provideLocalizationService(this);
    const { studyPlan, userIsAdministrator } = this.props;
    return [
      { id: STUDY_PLAN_ACTIONS.APPROVE, text: localizationService.toLanguageString('custom.approve'), disabled: studyPlan?.isApproved },
      { id: STUDY_PLAN_ACTIONS.EDIT, text: localizationService.toLanguageString('custom.edit'), disabled: studyPlan?.isApproved && !userIsAdministrator },
      { id: STUDY_PLAN_ACTIONS.DELETE, text: localizationService.toLanguageString('buttons.delete'), disabled: studyPlan?.studyPlanCycleCount }
    ];
  }
}

registerForLocalization(StudyPlanOverviewPage);

const mapStateToProps = state => ({
  userIsAdministrator: state.app.userIsAdministrator,
  studyPlan: state.studyPlanOverviewPage.studyPlan,
  loading: state.studyPlanOverviewPage.loading,
  studyPlanCycles: state.studyPlanCycleList.studyPlanCycles,
  isConfirmDialogVisible: state.studyPlanOverviewPage.isConfirmDialogVisible
});

const mapDispatchToProps = dispatch => ({
  loadStudyPlanOverviewPage: (payload) => dispatch(loadStudyPlanOverviewPage(payload)),
  approveStudyPlan: (payload) => dispatch(approveStudyPlan(payload)),
  deleteStudyPlan: (payload) => dispatch(deleteStudyPlan(payload)),
  toggleConfirmDialog: () => dispatch(toggleConfirmDialog())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(StudyPlanOverviewPage));
